/**
 * Shared, fetch-once hooks for skill levels and age categories — replaces the
 * hardcoded LEVELS/AGE arrays that used to be independently duplicated (with
 * drifting values) across BatchConfig.js, BatchManagement.js,
 * PerformanceUpload.js, PromotionReadinessReport.js, StudentRosterReport.js,
 * ParentDashboard.js, CategoryPerformanceReport.js, StudentMaster.js and
 * StudentPerformanceForm.js. Both read the same backend lookup tables
 * (skill_levels / age_categories) already exposed by config_master.py, so
 * editing those tables retunes every consumer with no code change.
 */
import { useEffect, useState } from "react";
import API_BASE from "../apiConfig";

let _skillLevelsCache = null;
let _ageCategoriesCache = null;

export function useSkillLevels() {
  const [levels, setLevels] = useState(_skillLevelsCache || []);
  useEffect(() => {
    if (_skillLevelsCache) return;
    fetch(`${API_BASE}/config/lookups/skill-levels`)
      .then(r => r.json())
      .then(d => {
        const rows = (d.data || []).filter(r => r.active !== 0);
        _skillLevelsCache = rows;
        setLevels(rows);
      })
      .catch(() => {});
  }, []);
  return levels; // [{level_id, level_name, level_order, color_hex, promotion_threshold}, ...]
}

export function useAgeCategories() {
  const [categories, setCategories] = useState(_ageCategoriesCache || []);
  useEffect(() => {
    if (_ageCategoriesCache) return;
    fetch(`${API_BASE}/config/lookups/age-categories`)
      .then(r => r.json())
      .then(d => {
        const rows = (d.data || []).filter(r => r.active !== 0);
        _ageCategoriesCache = rows;
        setCategories(rows);
      })
      .catch(() => {});
  }, []);
  return categories; // [{category_id, category_code, category_name, min_age, max_age, sort_order}, ...]
}

/** age (whole years) -> category_code, using the live age_categories brackets. */
export function categoryForAge(age, categories) {
  if (age == null || !categories?.length) return null;
  for (const c of categories) {
    const mn = c.min_age ?? -1;
    const mx = c.max_age;
    if (age >= mn && (mx == null || age < mx)) return c.category_code;
  }
  return null;
}

/** avg score (0-10) -> {label, color, min, max}, using live skill_levels promotion_threshold. */
export function bandForAvg(avg, levels) {
  if (!levels?.length) return null;
  let lo = 0;
  for (const lv of levels) {
    const threshold = lv.promotion_threshold != null ? Number(lv.promotion_threshold) : null;
    const hi = threshold != null ? threshold : 10;
    if (threshold == null || avg < threshold) {
      return { label: lv.level_name, color: lv.color_hex || "#3b82f6", min: lo, max: hi };
    }
    lo = threshold;
  }
  const last = levels[levels.length - 1];
  return { label: last.level_name, color: last.color_hex || "#3b82f6", min: lo, max: 10 };
}
